import { Box } from '@mantine/core'

import { CountCombobox } from '@components/ui/CountCombobox'
import { useSelectedAccounts } from '@data/accounts/accounts'
import Account from '@data/accounts/model/account'
import CustomerLocation from '@data/locations/model/customerLocation'
import {
  useLocationsLoading,
  useSelectedLocations,
  useVisibleLocations,
} from '@data/locations/locations'
import { useLocationStore } from '@store/location.store'

const LocationSelector = () => {
  const selectedAccounts = useSelectedAccounts()
  const locations = useVisibleLocations()
  const selectedLocations = useSelectedLocations()
  const loading = useLocationsLoading()
  const setSelectedLocationIds = useLocationStore((state) => state.setSelectedLocationIds)

  const locationGroups = selectedAccounts.map((account: Account) => ({
    label: account.accountName,
    options: locations
      .filter((location: CustomerLocation) => location.accountIdRef === account.id)
      .map((location: CustomerLocation) => ({
        value: location.id ?? '',
        label: location.name,
      })),
  }))
  const selectedLocationIds = selectedLocations.map((location) => location.id ?? '')
  const locationById = new Map(locations.map((location) => [location.id ?? '', location]))

  const handleSelectedLocationIdsChange = (ids: string[]) => {
    const selected = ids.filter((id) => locationById.has(id))

    // Selecting every visible location stores the default rather than a snapshot of it.
    //
    // `null` means "all visible locations" and is derived, so it keeps tracking the visible
    // set as accounts change. Writing the ids instead would look identical in the UI while
    // silently freezing the selection, and nothing in the interface can get back to `null`.
    // `useAccountSelection` applies the same rule to accounts, for the same reason.
    const isDefaultSelection = locations.length > 0 && selected.length === locations.length

    setSelectedLocationIds(isDefaultSelection ? null : selected)
  }

  return (
    locations.length > 1 && (
      <Box fz={'0.9em'} maw={'20em'}>
        <CountCombobox
          label={'Locations'}
          options={locationGroups}
          selectedValues={selectedLocationIds}
          onSelectedValuesChange={handleSelectedLocationIdsChange}
          loading={loading}
          disabled={loading || locations.length === 0 || selectedAccounts.length === 0}
        />
      </Box>
    )
  )
}

export default LocationSelector
